import { useQueries, useQuery } from '@tanstack/react-query'
import { QueryKeys } from 'constants/query-keys'
import {
    StoreFulfillmentPickupHistoricalProps,
    StoreFulfillmentProps,
    StoreFulfillmentShipHistoricalProps,
} from 'types/api'
import { TIME } from 'utils/misc'
import { useEssentials } from 'hooks/utils/useEssentials'
import { useAnalytics } from 'utils/analytics'
import {
    fetchStoreFulfillment,
    fetchStoreFulfillmentPickupHistorical,
    fetchStoreFulfillmentShipOnTime,
} from 'queries/store-fulfillment/store-fulfillment'
import {
    isStore,
    isDistrict,
    isRegion,
    isGroup,
    isCompany,
} from 'utils/location'
import { Constants } from 'constants/constants'
import { getGreenfieldLocationFilter } from 'utils/greenfield'
import { getShipOnTimeFilters } from 'pages/fulfillment/store-fulfillment/ship/ship-store-helpers'
import { StoreFulfillmentLocationData } from 'types/store-fulfillment'

const locationTypeToSubPath: Record<string, string> = {
    store: '/stores',
    district: '/districts',
    region: '/regions',
    group: '/groups',
}

const getLocationType = (location: string): string => {
    if (isStore(location)) return 'store'
    if (isDistrict(location)) return 'district'
    if (isRegion(location)) return 'region'
    if (isGroup(location)) return 'group'
    return ''
}

export function useStoreFulfillment({
    env,
    accessToken,
    filters,
    location,
}: StoreFulfillmentProps) {
    const { currentLocation } = useEssentials()
    const { tenMinutes } = TIME
    const { trackEvent } = useAnalytics()
    const numericLocation = currentLocation.replace(/\D/g, '')
    const dynamicSubPath =
        locationTypeToSubPath[getLocationType(currentLocation)] || ''

    const dynamicFilters = {
        ...(isStore(currentLocation) && { store_id: numericLocation }),
        ...(isDistrict(currentLocation) && { district_id: numericLocation }),
        ...(isRegion(currentLocation) && { region_id: numericLocation }),
        ...(isGroup(currentLocation) && { group_id: numericLocation }),
        date: new Date().toISOString().split('T')[0],
        ...filters,
    }

    const { data, isLoading, isError, refetch, remove, error } = useQuery<
        StoreFulfillmentLocationData[]
    >(
        [
            QueryKeys.STORE_FULFILLMENT,
            currentLocation,
            dynamicFilters,
            location,
        ],
        () =>
            fetchStoreFulfillment({
                env,
                accessToken,
                filters: dynamicFilters,
                trackEvent,
                dynamicSubPath,
            }),
        {
            staleTime: 0,
            refetchInterval: tenMinutes,
            retry: false,
        },
    )

    return { data, isLoading, isError, refetch, remove, error }
}

export function useFulFillmentPickupHistorical({
    env,
    accessToken,
    location,
    filters,
    enabled,
}: StoreFulfillmentPickupHistoricalProps) {
    const { tenMinutes } = TIME
    const { trackEvent } = useAnalytics()
    const isCompanyLocation = isCompany(location)

    const greenfieldCardNumbers = [
        Constants.GREENFIELD_CARDS.FULFILLMENT.HISTORICAL_PICKUP,
    ]

    const queries = greenfieldCardNumbers.map((cardNumber) => {
        const overviewFilters = {
            ...(!isCompanyLocation && getGreenfieldLocationFilter(location)),
            ...filters,
        }
        return {
            queryKey: [
                QueryKeys.STORE_FULFILLMENT_PICKUP_HISTORICAL,
                location,
                cardNumber,
                overviewFilters,
            ],
            queryFn: enabled
                ? () =>
                      fetchStoreFulfillmentPickupHistorical({
                          env,
                          accessToken,
                          dynamicSubPath: cardNumber,
                          method: 'GET',
                          filters: overviewFilters,
                          trackEvent,
                      })
                : undefined,
            staleTime: tenMinutes,
            cacheTime: tenMinutes,
            retry: false,
            enabled: enabled,
        }
    })

    const results = useQueries({ queries })
    const fulfillment = results[0]
    const storesHistoricalData = fulfillment?.data ?? null
    const isLoading = fulfillment?.isLoading || false
    const isError = fulfillment?.isError ?? false

    return {
        storesHistoricalData,
        isLoading,
        isError,
        error: fulfillment?.error,
        refetch: fulfillment?.refetch,
    }
}
export function useFulFillmentShipHistorical({
    env,
    accessToken,
    location,
    filters,
    enabled,
}: StoreFulfillmentShipHistoricalProps) {
    const { tenMinutes } = TIME
    const { trackEvent } = useAnalytics()
    const isCompanyLocation = isCompany(location)
    const overviewFilters = {
        ...(!isCompanyLocation && getGreenfieldLocationFilter(location)),
        ...filters,
    }
    const shipOnTimeFilters = getShipOnTimeFilters(
        location,
        filters.time_dimension,
    )
    const historicalQuery = {
        queryKey: [
            QueryKeys.STORE_FULFILLMENT_SHIP_HISTORICAL,
            location,
            Constants.GREENFIELD_CARDS.FULFILLMENT.HISTORICAL_SHIP,
            overviewFilters,
        ],
        queryFn: enabled
            ? () =>
                  fetchStoreFulfillmentPickupHistorical({
                      env,
                      accessToken,
                      dynamicSubPath:
                          Constants.GREENFIELD_CARDS.FULFILLMENT
                              .HISTORICAL_SHIP,
                      method: 'GET',
                      filters: overviewFilters,
                      trackEvent,
                  })
            : undefined,
        staleTime: tenMinutes,
        cacheTime: tenMinutes,
        retry: false,
        enabled,
    }
    const shipOnTimeQuery = {
        queryKey: [
            QueryKeys.STORE_FULFILLMENT_SHIP_ON_TIME,
            location,
            shipOnTimeFilters,
        ],
        queryFn: enabled
            ? () =>
                  fetchStoreFulfillmentShipOnTime({
                      env,
                      accessToken,
                      dynamicSubPath:
                          Constants.GREENFIELD_CARDS.FULFILLMENT.SHIP_ON_TIME,
                      filters: shipOnTimeFilters,
                      trackEvent,
                  })
            : undefined,
        staleTime: tenMinutes,
        cacheTime: tenMinutes,
        retry: false,
        enabled,
    }

    const results = useQueries({
        queries: [historicalQuery, shipOnTimeQuery],
    })
    const [shipHistorical, shipOnTime] = results

    return {
        storesHistoricalData: shipHistorical?.data ?? null,
        shipOnTimeData: shipOnTime?.data ?? null,
        isLoading: shipHistorical?.isLoading ?? false,
        isError: shipHistorical?.isError ?? false,
        isShipOnTimeLoading: shipOnTime?.isLoading ?? false,
        isShipOnTimeError: shipOnTime?.isError ?? false,
        shipOnTimeError: shipOnTime?.error,
        refetchShipOnTime: shipOnTime?.refetch,
    }
}
